import {SplitBookingDetails} from 'components/Offer/types'
import {listDisplayedTagsForOffer} from 'modules/analytics/utils'
import {OfferAccessFlowSettings} from 'modules/sapiSearch/constants'
import {HotelOfferEntity, SearchOffer} from 'modules/sapiSearch/slice'
import {getTopOffers, getTotalPrice} from 'modules/sapiSearch/utils'
import {isEmpty} from 'ramda'
import {
  getCheapestTopOfferIndex,
  getOfferPositionFromEntity
} from 'utils/offers'

import {SearchOfferContext} from '@vio/core/src/analytics/types/Events'
import {isPrivateDeal} from '@vio/core/src/auth/modules/utils'
import {getHasFreeCancellation} from '@vio/core/src/offer/business/cancellationPenalties'
import {calculateChargeDate} from '@vio/core/src/offer/business/chargeLater'
import {getMealsAmenities} from '@vio/core/src/offer/business/meal'
import {
  hasChargeLaterPayment,
  hasPayAtPropertyPayment
} from '@vio/core/src/offer/business/offerPaymentMode'
import {getChargeTypes, hasOfferTag} from '@vio/core/src/offer/business/offers'
import {OfferTagLabel} from '@vio/core/src/offer/types/offer'

export interface SplitBookingDataProps {
  splitBookingPosition: number
  isSplitBooking: boolean
  offerPositionInsideBundle: 'a' | 'b'
}

/**
 * Factory function that return split booking state and position from raw data
 * Also, it returns the bundleId and the split booking data for split booking
 */
const getSplitBookingContext = (
  offerId: string,
  hotelOfferEntity: HotelOfferEntity,
  splitBookingDetails: SplitBookingDetails
) => {
  const {hasSplitBookingOffer, splitBookingOffers} = splitBookingDetails || {}
  const {splitBooking} = hotelOfferEntity || {}
  const sbOfferIndex = splitBookingOffers?.findIndex(
    ({offer}) => offer.id === offerId
  )

  if (hasSplitBookingOffer && sbOfferIndex >= 0) {
    const hasFreeCancellation =
      getHasFreeCancellation(
        splitBookingOffers[0].offer?.cancellationPenalties
      ) &&
      getHasFreeCancellation(splitBookingOffers[1].offer?.cancellationPenalties)
    return {
      isSplitBooking: true,
      bundleId: splitBooking?.bundleId,
      splitBooking: {
        ...splitBooking,
        hasFreeCancellation
      }
    }
  }

  return {
    isSplitBooking: false
  }
}

/**
 * Build/compute offer context from raw data
 */
export const buildOfferContext = (
  offerId: string,
  sourceComponent: string,
  hotelOfferEntity: HotelOfferEntity,
  offer: SearchOffer,
  numberOfRooms: number,
  shouldSeeOffersUnlocked: boolean,
  splitBookingDetails: SplitBookingDetails,
  offerAccessFlowSettings: OfferAccessFlowSettings | null,
  isGreatDeal = false
): SearchOfferContext => {
  if (!offer || !hotelOfferEntity) return

  const {offerPosition, offerPositionFromOneWithSuffix} =
    getOfferPositionFromEntity(
      offerId,
      hotelOfferEntity,
      splitBookingDetails,
      sourceComponent
    )

  const {isSplitBooking, bundleId, splitBooking} = getSplitBookingContext(
    offerId,
    hotelOfferEntity,
    splitBookingDetails
  )

  const totalPrice = getTotalPrice(offer.rate, Number(numberOfRooms))
  const topOffers = getTopOffers(hotelOfferEntity)

  const cheapestOfferIndex = getCheapestTopOfferIndex(topOffers)

  const {feedID, providerRateType} = offer.metadata
  const providerFeedId = isEmpty(feedID) ? undefined : feedID
  const rateType = isEmpty(providerRateType) ? undefined : providerRateType
  const isCheapest = cheapestOfferIndex === offerPosition
  const isPrivateDealOffer = isPrivateDeal(offer, shouldSeeOffersUnlocked)
  const isAnchorPriceOffer = hasOfferTag(
    offer,
    OfferTagLabel.IsAnchorPriceOffer
  )
  const isTopOffer = hasOfferTag(offer, OfferTagLabel.IsTopOffer)
  const isCheapestRAA = hasOfferTag(offer, OfferTagLabel.IsCheapest)
  const meals = getMealsAmenities(offer)
  const hasFreeCancellation = getHasFreeCancellation(
    offer?.cancellationPenalties
  )

  const displayedTags = listDisplayedTagsForOffer({
    isCheapestOffer: isCheapest,
    isFirstOfferInList: offerPosition === 0,
    isGreatDeal
  })

  const isChargeLaterEligible = hasChargeLaterPayment(offer, isSplitBooking)
  const canPayLater = hasPayAtPropertyPayment(offer) || isChargeLaterEligible

  const chargeDate = calculateChargeDate(offer.cancellationPenalties)

  const chargeType = getChargeTypes({
    isExistingBooking: false,
    isChargeLaterEligible,
    isPayAtProperty: hasPayAtPropertyPayment(offer)
  })

  const paymentExpiryAt = {
    ...(isChargeLaterEligible && {chargeExpiryAt: chargeDate?.toISOString()})
  }
  const isAppExclusiveRate = offerAccessFlowSettings?.isAppRequired

  return {
    offerId,
    roomName: offer.roomName,
    meals,
    displayPricePerRoomPerNight: offer.nightlyRate,
    totalPrice,
    rateBreakdown: {
      baseRate: offer.rate.base,
      localTaxes: offer.rate.hotelFees,
      taxes: offer.rate.taxes
    },
    offerPosition: offerPositionFromOneWithSuffix,
    osoOfferPosition: isSplitBooking
      ? offerPositionFromOneWithSuffix
      : offer.osoOfferPosition,
    offerTags: offer.tags,
    availableRooms: offer.availableRooms,
    canPayLater,
    chargeType,
    hasFreeCancellation,
    isAnchorPriceOffer,
    isCheapest,
    isCheapestRAA,
    isPrivateDeal: isPrivateDealOffer,
    isSharedRoom: offer['isSharedRoom'],
    isStatic: false,
    isTopOffer,
    providerCode: offer.providerCode,
    // TODO: This field does not exist in the offer model (neither V3 and the old one)
    // and is always undefined. Should I clean it up?
    cancellationPolicy: offer['cancellationPolicy'],
    cancellationPenalties: offer.cancellationPenalties,
    bookURI: offer.url,
    cug: offer.accessTier ? [offer.accessTier] : null,
    providerFeedId,
    proxyProviderCode: offer.intermediaryProvider,
    providerRateType: rateType,
    displayedTags,
    numberOfRooms,
    isAppExclusiveRate,
    originalAccessTier: offer.metadata?.originalAccessTier,
    ...(isChargeLaterEligible && {paymentExpiryAt}),
    sourceComponent,
    // Split booking
    isSplitBooking,
    bundleId,
    splitBooking,
    ...(offerAccessFlowSettings && {
      ctaType: offerAccessFlowSettings.flowType,
      isAuthMovedToAP: offerAccessFlowSettings.isSpecial
        ? !offerAccessFlowSettings.isLocked
        : false
    })
  }
}
